import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { InvoiceCombinedData } from '../services/api/modern-invoice.service';
import ModernInvoicePrintButton from './ModernInvoicePrintButton';

interface ModernInvoicePreviewProps {
  invoiceId: string;
  language?: 'en' | 'de';
  showActions?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Modern Invoice Preview Component
 * 
 * Displays a preview of the modern invoice with combined data
 * and provides print/download actions
 */
const ModernInvoicePreview: React.FC<ModernInvoicePreviewProps> = ({
  invoiceId,
  language = 'en',
  showActions = true,
  className = '',
  style = {}
}) => {
  const [data, setData] = useState<InvoiceCombinedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCombinedData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const combinedData = await apiService.getCombinedInvoiceData(invoiceId);
      setData(combinedData);
    } catch (err: any) {
      console.error('Error fetching combined invoice data:', err);
      setError(err.message || 'Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    fetchCombinedData();
  }, [fetchCombinedData]);

  
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US');
  };

  const getStatusBadgeClass = (status: string) => {
    const statusClasses = {
      draft: 'badge-secondary',
      finalized: 'badge-info',
      paid: 'badge-success',
      overdue: 'badge-danger',
      cancelled: 'badge-warning'
    };
    return statusClasses[status.toLowerCase() as keyof typeof statusClasses] || 'badge-secondary';
  };

  // Inline styles object
  const styles = {
    modernInvoicePreview: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      fontFamily: "'Inter', sans-serif",
      ...style
    } as React.CSSProperties,
    
    previewHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '30px',
      paddingBottom: '20px',
      borderBottom: '2px solid #4176ba'
    } as React.CSSProperties,
    
    headerLeft: {
      flex: 1
    } as React.CSSProperties,
    
    invoiceTitle: {
      fontFamily: "'Montserrat', sans-serif",
      fontWeight: 800,
      fontSize: '24px',
      color: '#4176ba',
      margin: 0
    } as React.CSSProperties,
    
    invoiceNumber: {
      fontSize: '14px',
      color: '#363640',
      margin: '5px 0 0 0'
    } as React.CSSProperties,
    
    headerRight: {
      textAlign: 'right' as const
    },
    
    companyLogo: {
      maxHeight: '60px',
      maxWidth: '120px'
    } as React.CSSProperties,
    
    infoSection: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '30px',
      gap: '40px'
    } as React.CSSProperties,
    
    companyInfo: {
      flex: 1
    } as React.CSSProperties,
    
    customerInfo: {
      flex: 1
    } as React.CSSProperties,
    
    infoTitle: {
      fontFamily: "'Montserrat', sans-serif",
      fontWeight: 700,
      fontSize: '16px',
      color: '#4176ba',
      marginBottom: '10px',
      borderBottom: '1px solid #e2e3e7',
      paddingBottom: '5px'
    } as React.CSSProperties,
    
    infoContent: {
      fontSize: '14px',
      lineHeight: 1.4
    } as React.CSSProperties,
    
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      marginBottom: '20px'
    },
    
    detailsTable: {
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderRadius: '4px',
      overflow: 'hidden'
    } as React.CSSProperties,
    
    detailsTableTd: {
      padding: '12px 16px',
      borderBottom: '1px solid #e2e3e7',
      fontSize: '14px'
    } as React.CSSProperties,
    
    detailsTableTdFirst: {
      fontWeight: 500,
      color: '#4176ba',
      width: '40%'
    } as React.CSSProperties,
    
    linesTable: {
      fontSize: '14px'
    } as React.CSSProperties,
    
    linesTableThead: {
      background: '#4176ba',
      color: 'white'
    } as React.CSSProperties,
    
    linesTableTh: {
      padding: '12px',
      textAlign: 'left' as const,
      fontFamily: "'Montserrat', sans-serif",
      fontWeight: 700
    },
    
    linesTableTd: {
      padding: '12px',
      verticalAlign: 'top' as const,
      borderBottom: '1px solid #e2e3e7'
    },
    
    linesTableTdEven: {
      backgroundColor: '#f8f9fa'
    } as React.CSSProperties,
    
    lineDescription: {
      fontWeight: 500,
      marginBottom: '4px'
    } as React.CSSProperties,
    
    linePeriod: {
      fontSize: '12px',
      color: '#666',
      fontStyle: 'italic' as const
    },
    
    totalsSection: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: '30px'
    } as React.CSSProperties,
    
    totalsTable: {
      width: '50%',
      fontSize: '14px'
    } as React.CSSProperties,
    
    totalsTableTd: {
      padding: '8px 16px',
      borderBottom: '1px solid #e2e3e7'
    } as React.CSSProperties,
    
    totalsTableTdFirst: {
      textAlign: 'right' as const,
      fontWeight: 500
    },
    
    totalsTableTdLast: {
      textAlign: 'right' as const,
      width: '30%'
    },
    
    totalRow: {
      background: '#4176ba',
      color: 'white',
      fontWeight: 700,
      fontSize: '16px'
    } as React.CSSProperties,
    
    totalRowTd: {
      borderBottom: 'none'
    } as React.CSSProperties,
    
    previewActions: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: '20px',
      borderTop: '1px solid #e2e3e7'
    } as React.CSSProperties,
    
    languageToggle: {
      display: 'flex',
      gap: '5px'
    } as React.CSSProperties,
    
    badge: {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 500,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px'
    } as React.CSSProperties,
    
    loadingSpinner: {
      textAlign: 'center' as const,
      padding: '60px 20px'
    },
    
    errorMessage: {
      textAlign: 'center' as const,
      padding: '60px 20px'
    },
    
    spinner: {
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #4176ba',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 20px'
    } as React.CSSProperties,
    
    textRight: {
      textAlign: 'right' as const
    }
  };

  if (loading) {
    return (
      <div className={`modern-invoice-preview loading ${className}`} style={styles.modernInvoicePreview}>
        <div style={styles.loadingSpinner}>
          <div style={styles.spinner}></div>
          <p>Loading invoice data...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`modern-invoice-preview error ${className}`} style={styles.modernInvoicePreview}>
        <div style={styles.errorMessage}>
          <h3>❌ Error Loading Invoice</h3>
          <p>{error || 'Unknown error occurred'}</p>
          <button className="btn btn-secondary" onClick={fetchCombinedData}>
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`modern-invoice-preview ${className}`} style={styles.modernInvoicePreview}>
      {/* Header */}
      <div style={styles.previewHeader}>
        <div style={styles.headerLeft}>
          <h2 style={styles.invoiceTitle}>
            {language === 'de' ? 'RECHNUNG' : 'INVOICE'}
          </h2>
          <p style={styles.invoiceNumber}>{data.invoice.invoice_name}</p>
        </div>
        <div style={styles.headerRight}>
          {data.company.logo_url && (
            <img 
              src={data.company.logo_url} 
              alt={`${data.company.name} Logo`}
              style={styles.companyLogo}
            />
          )}
        </div>
      </div>

      {/* Company and Customer Info */}
      <div style={styles.infoSection}>
        <div style={styles.companyInfo}>
          <h3 style={styles.infoTitle}>{language === 'de' ? 'Rechnungssteller' : 'From'}</h3>
          <div style={styles.infoContent}>
            <strong>{data.company.name}</strong><br />
            {data.company.address_street && <>{data.company.address_street}<br /></>}
            {data.company.address_postcode && data.company.address_city && (
              <>{data.company.address_postcode} {data.company.address_city}<br /></>
            )}
            {data.company.address_country && <>{data.company.address_country}<br /></>}
            {data.company.email_address && (
              <><br />Email: {data.company.email_address}</>
            )}
            {data.company.telephone_number && (
              <><br />Phone: {data.company.telephone_number}</>
            )}
          </div>
        </div>

        <div style={styles.customerInfo}>
          <h3 style={styles.infoTitle}>{language === 'de' ? 'Rechnungsempfänger' : 'Bill To'}</h3>
          <div style={styles.infoContent}>
            <strong>{data.customer.customer_name}</strong><br />
            {data.customer.customer_contact_name && (
              <>{language === 'de' ? 'z.Hd.' : 'Attn:'} {data.customer.customer_contact_name}
              {data.customer.customer_contact_role && ` (${data.customer.customer_contact_role})`}<br /></>
            )}
            {data.customer.customer_address_street && <>{data.customer.customer_address_street}<br /></>}
            {data.customer.customer_address_postcode && data.customer.customer_address_city && (
              <>{data.customer.customer_address_postcode} {data.customer.customer_address_city}<br /></>
            )}
            {data.customer.customer_address_country && <>{data.customer.customer_address_country}<br /></>}
            {data.customer.customer_email_address && (
              <><br />Email: {data.customer.customer_email_address}</>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Details */}
      <div className="invoice-details">
        <table style={{...styles.table, ...styles.detailsTable}}>
          <tbody>
            <tr>
              <td style={{...styles.detailsTableTd, ...styles.detailsTableTdFirst}}>
                {language === 'de' ? 'Rechnungsnummer' : 'Invoice Number'}
              </td>
              <td style={styles.detailsTableTd}><strong>{data.invoice.invoice_name}</strong></td>
            </tr>
            <tr>
              <td style={{...styles.detailsTableTd, ...styles.detailsTableTdFirst}}>
                {language === 'de' ? 'Rechnungsdatum' : 'Invoice Date'}
              </td>
              <td style={styles.detailsTableTd}>{formatDate(data.invoice.invoice_date)}</td>
            </tr>
            <tr>
              <td style={{...styles.detailsTableTd, ...styles.detailsTableTdFirst}}>
                {language === 'de' ? 'Fälligkeitsdatum' : 'Due Date'}
              </td>
              <td style={styles.detailsTableTd}><strong>{formatDate(data.invoice.due_date)}</strong></td>
            </tr>
            <tr>
              <td style={{...styles.detailsTableTd, ...styles.detailsTableTdFirst}}>
                {language === 'de' ? 'Abrechnungszeitraum' : 'Billing Period'}
              </td>
              <td style={styles.detailsTableTd}>
                {formatDate(data.invoice.billing_period_start)} - {formatDate(data.invoice.billing_period_end)}
              </td>
            </tr>
            <tr>
              <td style={{...styles.detailsTableTd, ...styles.detailsTableTdFirst}}>Status</td>
              <td style={styles.detailsTableTd}>
                <span className={`badge ${getStatusBadgeClass(data.invoice.invoice_status)}`} style={styles.badge}>
                  {data.invoice.invoice_status.toUpperCase()}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Invoice Lines */}
      <div className="invoice-lines">
        <table style={{...styles.table, ...styles.linesTable}}>
          <thead style={styles.linesTableThead}>
            <tr>
              <th style={styles.linesTableTh}>{language === 'de' ? 'Beschreibung' : 'Description'}</th>
              <th style={styles.linesTableTh}>{language === 'de' ? 'Menge' : 'Qty'}</th>
              <th style={styles.linesTableTh}>{language === 'de' ? 'Einzelpreis' : 'Unit Price'} ({data.invoice.currency})</th>
              <th style={styles.linesTableTh}>{language === 'de' ? 'Gesamt' : 'Total'} ({data.invoice.currency})</th>
            </tr>
          </thead>
          <tbody>
            {data.invoice_lines.map((line, index) => (
              <tr key={line.invoiceline_id} style={index % 2 === 1 ? styles.linesTableTdEven : {}}>
                <td style={styles.linesTableTd}>
                  <div style={styles.lineDescription}>{line.description}</div>
                  <div style={styles.linePeriod}>
                    {language === 'de' ? 'Zeitraum:' : 'Period:'} {formatDate(line.line_billing_start)} - {formatDate(line.line_billing_end)}
                  </div>
                </td>
                <td style={{...styles.linesTableTd, ...styles.textRight}}>{line.quantity}</td>
                <td style={{...styles.linesTableTd, ...styles.textRight}}>{line.unit_price.toFixed(2)}</td>
                <td style={{...styles.linesTableTd, ...styles.textRight}}>{line.line_total.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div style={styles.totalsSection}>
        <table style={{...styles.table, ...styles.totalsTable}}>
          <tbody>
            <tr>
              <td style={{...styles.totalsTableTd, ...styles.totalsTableTdFirst}}>
                {language === 'de' ? 'Zwischensumme' : 'Subtotal'}
              </td>
              <td style={{...styles.totalsTableTd, ...styles.totalsTableTdLast}}>
                {formatCurrency(data.invoice.subtotal, data.invoice.currency)}
              </td>
            </tr>
            {data.invoice.tax_rate > 0 && (
              <tr>
                <td style={{...styles.totalsTableTd, ...styles.totalsTableTdFirst}}>
                  {language === 'de' ? 'MwSt.' : 'Tax'} ({data.invoice.tax_rate.toFixed(1)}%)
                </td>
                <td style={{...styles.totalsTableTd, ...styles.totalsTableTdLast}}>
                  {formatCurrency(data.invoice.tax_amount, data.invoice.currency)}
                </td>
              </tr>
            )}
            <tr style={styles.totalRow}>
              <td style={{...styles.totalsTableTd, ...styles.totalsTableTdFirst, ...styles.totalRowTd}}>
                <strong>{language === 'de' ? 'Gesamtbetrag' : 'Total Amount'}</strong>
              </td>
              <td style={{...styles.totalsTableTd, ...styles.totalsTableTdLast, ...styles.totalRowTd}}>
                <strong>{formatCurrency(data.invoice.total_amount, data.invoice.currency)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Actions */}
      {showActions && (
        <div style={styles.previewActions}>
          <ModernInvoicePrintButton
            invoiceId={invoiceId}
            language={language}
            variant="both"
            className="btn-lg"
          />
          
          {/* Language Toggle */}
          <div style={styles.languageToggle}>
            <button
              className={`btn btn-sm ${language === 'en' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => window.location.href = `?language=en`}
            >
              🇺🇸 English
            </button>
            <button
              className={`btn btn-sm ${language === 'de' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => window.location.href = `?language=de`}
            >
              🇩🇪 Deutsch
            </button>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media (max-width: 768px) {
            .modern-invoice-preview .info-section {
              flex-direction: column !important;
              gap: 20px !important;
            }
            
            .modern-invoice-preview .preview-actions {
              flex-direction: column !important;
              gap: 15px !important;
            }
            
            .modern-invoice-preview .totals-table {
              width: 100% !important;
            }
          }
        `
      }} />
    </div>
  );
};

export default ModernInvoicePreview;